export type TradePage = {
  slug: string;
  label: string;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  headline: string;
  subhead: string;
  bullets: { title: string; body: string }[];
  howItRuns: string;
};

export const TRADE_PAGES: TradePage[] = [
  {
    slug: "general-contractor",
    label: "General contractor",
    metaTitle: "General contractor estimate software",
    metaDescription:
      "Build master estimates with subs, allowances, and owner selections. One portal link for approvals, change orders, and scheduling.",
    eyebrow: "General contractor",
    headline: "One master estimate for subs, allowances, and owner selections",
    subhead:
      "Framing, trades, and finishes in order, with change orders and approvals on the same customer link.",
    bullets: [
      {
        title: "Subs and allowances as line items",
        body: "Spell out who does what, what's allowance vs. fixed, and what the customer still needs to pick before work starts.",
      },
      {
        title: "Change orders on the same portal link",
        body: "Add scope without emailing revision 4. The customer sees the updated total and signs off in one place.",
      },
      {
        title: "Phases on the shared calendar",
        body: "Schedule rough-in, inspections, and finishes so office, subs, and the customer aren't chasing different spreadsheets.",
      },
    ],
    howItRuns:
      "Build the master estimate with subs, allowances, and owner selections as line items. Share one portal link for approvals and change orders. Schedule phases on the calendar so the customer and your subs aren't chasing different spreadsheets.",
  },
  {
    slug: "hvac",
    label: "HVAC",
    metaTitle: "HVAC estimate software",
    metaDescription:
      "Quote good, better, and best system options in one link. Tonnage, SEER, warranty, and labor spelled out for the customer.",
    eyebrow: "HVAC",
    headline: "Good, better, and best options in one customer link",
    subhead: "Tonnage, SEER, warranty, and install labor side by side, not three separate PDFs.",
    bullets: [
      {
        title: "Tiered packages as line items",
        body: "Put together good / better / best system options with install labor and warranty called out on each tier.",
      },
      {
        title: "Customer compares on the portal",
        body: "They open one link, compare packages side by side, and pick a tier. You're not hoping they open the right attachment.",
      },
      {
        title: "Deposit and schedule from the same record",
        body: "When they accept, connect payouts to collect a deposit and schedule the crew without re-entering the job.",
      },
    ],
    howItRuns:
      "Put together good / better / best system options as separate line items. The customer opens one link, compares the tiers side by side, and picks a package.",
  },
  {
    slug: "plumbing",
    label: "Plumbing",
    metaTitle: "Plumbing estimate software",
    metaDescription:
      "Quote water heater swaps, repipes, and rough-ins with photos and notes. Customer approves and pays the deposit on one link.",
    eyebrow: "Plumbing",
    headline: "Quote the swap or rough-in, get approval, and schedule the crew",
    subhead: "Photos, scope notes, and pricing on the same job, not buried in texts.",
    bullets: [
      {
        title: "Scope and photos on the quote",
        body: "Quote the water heater swap, repipe, or rough-in with site photos and notes attached to the same estimate.",
      },
      {
        title: "Portal approval and deposit",
        body: "They approve on the customer link and pay the deposit when you connect payouts, before you truck roll.",
      },
      {
        title: "One record from quote to job",
        body: "Schedule the crew from the accepted quote so nobody is digging through messages for \"which estimate was that?\"",
      },
    ],
    howItRuns:
      "Quote the water heater swap, repipe, or rough-in with photos and notes on the same job. They approve on the portal, pay the deposit if you take cards, and you schedule the crew from the same record.",
  },
  {
    slug: "electrical",
    label: "Electrical",
    metaTitle: "Electrical estimate software",
    metaDescription:
      "Panel upgrades, circuits, and permits as line items. Optional add-ons like EV charger prep on the customer view.",
    eyebrow: "Electrical",
    headline: "Panel upgrades, circuits, and add-ons before you pull permit",
    subhead: "Allowances and optional upgrades decided on the link, not as a change order on site.",
    bullets: [
      {
        title: "Permit and panel scope spelled out",
        body: "List the panel upgrade, circuits, and permit as line items with allowances called out clearly.",
      },
      {
        title: "Optional add-ons on the customer view",
        body: "Offer EV charger prep or whole-home surge as optional lines they can select before you return for extras.",
      },
      {
        title: "Signed scope before truck roll",
        body: "They sign off on the written scope in the portal so upgrades get decided upfront.",
      },
    ],
    howItRuns:
      "List the panel upgrade, circuits, and permit as line items, with allowances called out clearly. Offer add-ons as optional lines they can tick on the customer view.",
  },
  {
    slug: "roofing",
    label: "Roofing",
    metaTitle: "Roofing estimate software",
    metaDescription:
      "Storm and insurance templates with squares, tear-off, and photos. Send while the claim clock is ticking.",
    eyebrow: "Roofing",
    headline: "Insurance and storm scopes sent while the clock is ticking",
    subhead: "Squares, tear-off, ice & water, and ladder photos in one approved scope for office, sales, and crew.",
    bullets: [
      {
        title: "Start from your storm template",
        body: "Drop in squares, tear-off, ice & water, and photos from the ladder without rebuilding the estimate in Word.",
      },
      {
        title: "One approved scope for everyone",
        body: "Office, sales, and the crew all see the same approved scope and price. No version 3 in someone's inbox.",
      },
      {
        title: "Customer link for adjuster-friendly PDFs",
        body: "Export a polished PDF or send the live link when the homeowner or adjuster needs a paper trail.",
      },
    ],
    howItRuns:
      "Start from your storm or insurance template, drop in squares, tear-off, ice & water, and photos from the ladder. Send while the claim clock is ticking.",
  },
  {
    slug: "landscaping",
    label: "Landscaping",
    metaTitle: "Landscaping estimate software",
    metaDescription:
      "Phase grading, hardscape, and plantings with optional upgrades. Customers see what's included now vs. what costs extra.",
    eyebrow: "Landscaping",
    headline: "Phased hardscape and plantings with clear optional upgrades",
    subhead: "Break the job into sections so upsells don't mean rebuilding the whole estimate.",
    bullets: [
      {
        title: "Phases as separate sections",
        body: "Grading, hardscape, and plantings as distinct blocks with optional upgrades (lighting, irrigation, bigger trees).",
      },
      {
        title: "Included vs. extra is obvious",
        body: "The customer sees what's in this phase vs. what costs extra before they approve.",
      },
      {
        title: "Schedule phases on the calendar",
        body: "Turn accepted scope into scheduled work so crews know what's locked in for each visit.",
      },
    ],
    howItRuns:
      "Break the job into phases as separate sections with optional upgrades. The customer sees what's included now vs. what costs extra.",
  },
  {
    slug: "painting",
    label: "Painting",
    metaTitle: "Painting estimate software",
    metaDescription:
      "Quote by room or surface with prep level, coats, and who supplies paint. Customer signs off on scope in the portal.",
    eyebrow: "Painting",
    headline: "Scope prep level and coats before the first brush stroke",
    subhead:
      "Quote by room or surface with spot patch versus full skim, coats, and who supplies paint. Color notes live on the quote they approve.",
    bullets: [
      {
        title: "Prep level spelled out per area",
        body: "Spot patch, skim, or full prep on each section so \"I thought you were sanding everything\" never comes up at walkthrough.",
      },
      {
        title: "Customer-supplied paint called out",
        body: "Note when they buy material versus when you supply so margin stays where you expect.",
      },
      {
        title: "Portal sign-off on scope",
        body: "They approve the written scope on the link so touch-up arguments stay rare.",
      },
    ],
    howItRuns:
      "Quote by room or surface with prep level, coats, and who supplies paint. Color selections and confirmation notes live on the quote they sign in the portal.",
  },
  {
    slug: "handyman",
    label: "Handyman",
    metaTitle: "Handyman estimate software",
    metaDescription:
      "Quote small jobs with clear line items, trip fees, and exclusions. Customers approve on a phone link so punch lists do not live in text threads.",
    eyebrow: "Handyman",
    headline: "Small jobs quoted clean, not buried in texts",
    subhead:
      "Mounts, patches, fixture swaps, and punch lists as plain line items. Trip fees, exclusions, and add-ons stay visible before they say yes.",
    bullets: [
      {
        title: "Line items for mixed little jobs",
        body: "List each task, parts, and labor so a hallway light and two drywall patches do not turn into one vague number.",
      },
      {
        title: "Trip fee and exclusions up front",
        body: "Show the service call, what is not included, and what happens if they add work after you arrive.",
      },
      {
        title: "Approval on a phone link",
        body: "They review and accept the written scope so you are not chasing yeses across messages.",
      },
    ],
    howItRuns:
      "Build the quote with tasks, materials, and any trip fee as separate lines. Share one link for approval, then schedule the visit once the scope is locked.",
  },
  {
    slug: "cleaning",
    label: "Cleaning",
    metaTitle: "Cleaning estimate software",
    metaDescription:
      "Recurring or one-off cleans by square footage, rooms, or hours. Customers approve scope and pay the deposit on the link.",
    eyebrow: "Cleaning",
    headline: "Recurring or one-off cleans priced by scope, not guesswork",
    subhead: "Square footage, room count, or hours, with add-ons like windows or post-construction spelled out.",
    bullets: [
      {
        title: "Scope by sq ft, rooms, or hours",
        body: "Build recurring or one-off quotes with optional add-ons (windows, deep clean, post-construction) as line items.",
      },
      {
        title: "Customer picks a date and pays deposit",
        body: "They approve scope on the link and pay the deposit when you connect payouts.",
      },
      {
        title: "Crew sees locked-in scope",
        body: "Your team sees the same approved scope on the calendar. No \"we didn't quote the garage\" surprises.",
      },
    ],
    howItRuns:
      "Build recurring or one-off quotes by square footage, room count, or hours, with optional add-ons. Customers approve scope, pick a date, and pay the deposit on the link.",
  },
  {
    slug: "it-services",
    label: "IT / managed services",
    metaTitle: "IT & managed services estimate software",
    metaDescription:
      "Separate project setup from recurring monitoring and support. Customers sign off and pay the deposit through the portal.",
    eyebrow: "IT / managed services",
    headline: "Project work and recurring service on separate, clear line items",
    subhead: "Setup, monitoring, hourly support, and hardware so the customer sees project vs. ongoing service.",
    bullets: [
      {
        title: "Project vs. recurring spelled out",
        body: "Setup, monitoring, hourly support, and hardware as separate lines so scope is obvious before kickoff.",
      },
      {
        title: "Portal sign-off and deposit",
        body: "They accept terms and pay the deposit through the customer link. No chasing PO emails to start.",
      },
      {
        title: "Jobs track delivery",
        body: "Turn accepted quotes into jobs so implementation and follow-up stay tied to what was sold.",
      },
    ],
    howItRuns:
      "Spell out setup, recurring monitoring, hourly support, and hardware as separate line items. They sign off and pay the deposit through the portal.",
  },
  {
    slug: "consulting",
    label: "Consulting / agency",
    metaTitle: "Consulting & agency proposal software",
    metaDescription:
      "Scope discovery, deliverables, and milestones as line items. Compare retainer vs. project pricing on one link.",
    eyebrow: "Consulting / agency",
    headline: "Retainer vs. project scope compared on one approval link",
    subhead: "Discovery, deliverables, and milestones with clear options. Accept terms and pay deposit without a contract round-trip.",
    bullets: [
      {
        title: "Milestones as line items",
        body: "Scope discovery, deliverables, and milestones with clear options the customer can compare on one link.",
      },
      {
        title: "Retainer or project pricing side by side",
        body: "Show both structures when it helps the client decide, without rebuilding the proposal in a doc tool.",
      },
      {
        title: "Kick off after deposit",
        body: "They accept terms and pay the deposit so you can start without another email thread.",
      },
    ],
    howItRuns:
      "Scope discovery, deliverables, and milestones as line items with clear options. Customers compare retainer vs. project pricing on one link.",
  },
];

export const TRADE_PAGE_BY_SLUG = Object.fromEntries(TRADE_PAGES.map((t) => [t.slug, t])) as Record<
  string,
  TradePage
>;

export function getTradePage(slug: string): TradePage | undefined {
  return TRADE_PAGE_BY_SLUG[slug];
}
